import React from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowRight, LinkExternal } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button/Button'
import { ExampleBrands } from './ExampleBrands/ExampleBrands'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { useBrandKitContext } from './BrandKitContext'
import Styles from './BrandKitForm.module.scss'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import cx from 'classnames'

interface IntegrationFailedBrandKitFormProps {
  failedIntegration: { id: number; name: string; provider: ExternalIntegrationEnum }
  title?: string
}

/**
 * Renders fallback UI if an integration (e.g. Instagram) fails to connect.
 */
export const IntegrationFailedBrandKitForm: React.FC<IntegrationFailedBrandKitFormProps> = ({
  failedIntegration,
  title,
}) => {
  const {
    url,
    isDirty,
    isValid,
    errorMessage,
    isBlank,
    inputProps,
    onGenerateFromWebsite,
    onGenerateFromExampleBrand,
  } = useBrandKitContext()

  const openWindow = useOpenWindowSafely()

  const openConnectionGuide = () => {
    switch (failedIntegration.provider) {
      case ExternalIntegrationEnum.Instagram:
        openWindow('https://help.blaze.ai/en/articles/9556524-connect-blaze-to-instagram-business')
        break
      case ExternalIntegrationEnum.Facebook:
        openWindow('https://help.blaze.ai/en/articles/9556888-connect-blaze-to-facebook-business')
        break
      case ExternalIntegrationEnum.LinkedIn:
        openWindow('https://help.blaze.ai/en/articles/9559677-connect-blaze-to-linkedin')
        break
      default:
    }
  }

  return (
    <MultiStepStepLayout title={title}>
      <div className={cx(Styles.container, Styles.integrationFailed)}>
        {/* 1. Open connection guide */}

        <div className={Styles.section}>
          <Text color="var(--dark-80">
            Please check your account permissions and try again. Need help? Check out our step-by-step connection guide.
          </Text>
          <Button variant="tertiary" onPress={openConnectionGuide} size="lg" fullWidth endIcon={LinkExternal}>
            Open Connection Guide
          </Button>
        </div>

        {/* 2. Website fallback */}
        <div className={Styles.section}>
          <Text color="var(--dark-80)">You can try using your website</Text>
          <div className={Styles.websiteSection}>
            <Input
              {...inputProps}
              value={url}
              errorMessage={isDirty && !isValid ? errorMessage : undefined}
              placeholder="Enter your website"
              size="lg"
              className={Styles.websiteField}
            />
            <div className={Styles.button}>
              <IconButton
                variant="primary"
                size="lg"
                onPress={onGenerateFromWebsite}
                isDisabled={isBlank}
                icon={ArrowRight}
              />
            </div>
          </div>
          <Text variant="metadata" color="var(--dark-60)">
            No website? Use an industry leader's site you like and adjust the Brand Kit to your liking.
          </Text>
        </div>

        {/* 3. Sample brands fallback */}
        <div className={Styles.section}>
          <Text color="var(--dark-80)">Or just create a Brand Kit using one of these sample brands.</Text>
          <ExampleBrands onExampleBrandSelect={onGenerateFromExampleBrand} />
        </div>
      </div>
    </MultiStepStepLayout>
  )
}
